import { useMemo, useState } from 'react';

import { commitNumberInput, clamp, handleArrowCommit, handleRangeIntChange } from './helpers.js';
import FitFacts from './FitFacts.js';

const calculateBmi = ({ weight, feet, inches }) => {
	const weightValue = parseFloat(weight);
	const heightFeet = parseFloat(feet);
	const heightInch = parseFloat(inches);
	const totalInches = parseFloat((heightFeet * 12) + heightInch);

	const meters = totalInches / 39.36;
	const kilos = weightValue / 2.2;
	const square = meters * meters;
	const bmi = parseFloat(kilos / square);

	if (!Number.isFinite(bmi)) {
		return '0';
	}

	return bmi.toString().substring(0, 6);
};

export default function BMI() {
	const [weight, setWeight] = useState(145);
	const [feet, setFeet] = useState(5);
	const [inches, setInches] = useState(7);
	const [weightInput, setWeightInput] = useState('145');
	const [feetInput, setFeetInput] = useState('5');
	const [inchesInput, setInchesInput] = useState('7');

	const bmiValue = useMemo(
		() => calculateBmi({ weight, feet, inches }),
		[weight, feet, inches]
	);

	return (
		<div className="calc-container-scroller">
			<div className="calc-container-scroller-inner">
				<div className="calculator-wrap">
					<h3>BMI Calculator</h3>
					<p>Use the sliders below to calculate your BMI:</p>

					<div className="calculator-row">
						<div className="range-row">
							<div className="range-row__input-wrap">
								<input
									id="range-weight"
									type="range"
									min="50"
									max="300"
									value={weight}
									onChange={(e) => handleRangeIntChange(e, setWeight, setWeightInput)}
								/>
							</div>

							<div className="range-row__result-wrap">
								<label htmlFor="result-weight">Weight: </label>
								<input
									type="number"
									id="result-weight"
									name="result-weight"
									min="50"
									max="300"
									value={weightInput}
									onChange={(e) => setWeightInput(e.target.value)}
									onKeyUp={(e) => handleArrowCommit(e, 50, 300, weight, setWeight, setWeightInput)}
									onBlur={() => commitNumberInput(weightInput, 50, 300, weight, setWeight, setWeightInput)}
								/>
							</div>
						</div>
					</div>

					<div className="calculator-row">
						<div className="range-row">
							<div className="range-row__input-wrap">
								<input
									id="range-feet"
									type="range"
									min="3"
									max="8"
									value={feet}
									onChange={(e) => handleRangeIntChange(e, setFeet, setFeetInput)}
								/>
							</div>

							<div className="range-row__result-wrap">
								<label htmlFor="result-feet">Height: </label>
								<input
									type="number"
									id="result-feet"
									name="result-feet"
									min="3"
									max="8"
									value={feetInput}
									onChange={(e) => setFeetInput(e.target.value)}
									onKeyUp={(e) => handleArrowCommit(e, 3, 8, feet, setFeet, setFeetInput)}
									onBlur={() => commitNumberInput(feetInput, 3, 8, feet, setFeet, setFeetInput)}
								/>
							</div>
						</div>
					</div>

					<div className="calculator-row">
						<div className="range-row">
							<div className="range-row__input-wrap">
								<input
									id="range-inches"
									type="range"
									min="0"
									max="11"
									value={inches}
									onChange={(e) => handleRangeIntChange(e, setInches, setInchesInput)}
								/>
							</div>

							<div className="range-row__result-wrap">
								<label htmlFor="result-inches">Inches: </label>
								<input
									type="number"
									id="result-inches"
									name="result-inches"
									min="0"
									max="11"
									value={inchesInput}
									onChange={(e) => setInchesInput(e.target.value)}
									onKeyUp={(e) => handleArrowCommit(e, 0, 11, inches, setInches, setInchesInput)}
									onBlur={() => commitNumberInput(inchesInput, 0, 11, inches, setInches, setInchesInput)}
								/>
							</div>
						</div>
					</div>

					<hr />

					<div className="results-row">
						<h4 className="results-row__label">
							Your estimated Body Mass Index based on the values above:
						</h4>

						<input
							type="text"
							className="results-row__value"
							readOnly
							id="result-bmi"
							name="result-bmi"
							value={bmiValue}
						/>
					</div>

					<FitFacts
						items={[
							{
								label: 'Numbers to Know: Body Mass Index',
								link: '/images/webcontent/assets/education-resources/lifestyle/fitfacts/pdfs/fitfacts/itemid_3594.pdf'
							}
						]}
					/>
				</div>
			</div>
		</div>
	);
}
